import { MoreOutlined, EyeOutlined } from "@ant-design/icons";
import { Typography, Space, Dropdown, Menu, Button, Tooltip } from "antd";
import clsx from "clsx";
import { journalsQueries } from "@/entities/journal";
import { ConnectIcon } from "@/shared/assets/svg/icons";
import { Can } from "@/shared/lib/casl";
import { queryClient } from "@/shared/lib/react-query";
import {
  CommentChip,
  UserBubble,
  RecordStatus,
  statusIcons,
  type CompanyItem,
} from "@/shared/ui";
import { JournalCryptographyWrapper } from "@shared/ui";
import { fallbackString, formatDate, formatMoscowTime } from "@shared/utils";
import { getSectionThreeActionItems } from "./actions-map.tsx";
import "./table-columns.scss";

const { Text } = Typography;

const COLUMN_TOOLTIPS = {
  workName:
    "Наименование работ, выполняемых в процессе строительства, реконструкции, капитального ремонта объекта капитального строительства",
  workType: "Тип выполняемых работ: работа или испытание",
  workDate: "Дата выполнения работ",
  location: "Местоположение",
  actConnect: "Связь с исполнительной документацией",
  signer: "Уполномоченный представитель лица, осуществляющего строительство",
  companies: "Организация, выполнившая работу",
};

const renderColumnTitle = (label: string, tooltip?: string) =>
  tooltip ? (
    <Tooltip title={tooltip}>
      <span className="journals-section-third-table__column-title">{label}</span>
    </Tooltip>
  ) : (
    label
  );

const renderDatetimeCell = (date?: string | null) => {
  if (!date) {
    return "-";
  }

  const formattedDate = formatDate(date);
  const formattedTime = formatMoscowTime(date);

  if (!formattedDate && !formattedTime) {
    return "-";
  }

  return (
    <div className="journals-section-third-table__datetime">
      {formattedDate ? (
        <span className="journals-section-third-table__datetime-date">
          {formattedDate}
        </span>
      ) : null}
      {formattedTime ? (
        <span className="journals-section-third-table__datetime-time">
          {formattedTime} МСК
        </span>
      ) : null}
    </div>
  );
};

const renderNumberCell = (record: { number?: number; updateDate?: string }) => {
  const { number, updateDate } = record;

  return (
    <div className="journals-section-third-table__number">
      <span className="journals-section-third-table__number-value">
        {number ?? "-"}
      </span>
      {updateDate ? (
        <>
          <span className="journals-section-third-table__number-updated">
            изменено {formatDate(updateDate)}
          </span>
          {formatMoscowTime(updateDate) ? (
            <span className="journals-section-third-table__datetime-time">
              {formatMoscowTime(updateDate)} МСК
            </span>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

const getOrganizationsLabel = (count: number) => {
  const mod10 = count % 10;
  const mod100 = count % 100;

  if (mod10 === 1 && mod100 !== 11) {
    return "организация";
  }

  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return "организации";
  }

  return "организаций";
};

const getCompanyName = (company?: CompanyItem) =>
  fallbackString(company?.shortName || company?.fullName);

const renderCompaniesList = (companies: CompanyItem[]) => {
  if (!companies.length) {
    return <Text type="secondary">—</Text>;
  }

  const firstCompany = companies[0];
  const remainingCompanies = companies.slice(1);

  return (
    <div className="journals-section-third-table__companies">
      <span className="journals-section-third-table__companies-name journals-section-third-table__text">
        {getCompanyName(firstCompany)}
      </span>
      {remainingCompanies.length > 0 ? (
        <Tooltip
          title={
            <div>
              {remainingCompanies.map((company, index) => (
                <div
                  key={company.companyId ?? index}
                  className="journals-section-third-table__companies-tooltip-item"
                >
                  {getCompanyName(company)}
                </div>
              ))}
            </div>
          }
        >
          <button type="button" className="journals-section-third-table__companies-more">
            еще {remainingCompanies.length}{" "}
            {getOrganizationsLabel(remainingCompanies.length)}
          </button>
        </Tooltip>
      ) : null}
    </div>
  );
};

const TableCellActions = ({
  record,
  handleAction,
  handleOpenFormInfo,
  handleOpenForm,
  onOpenVersionsModal,
  isVersionsEnabled = true,
}: {
  record: any;
  handleAction?: (action: string, actionPayload?: any) => Promise<void>;
  handleOpenFormInfo: (itemNoVersionId: string) => void;
  handleOpenForm: ({
    initTitle,
    isEdit,
    itemNoVersionId,
  }: {
    initTitle: string;
    isEdit?: boolean;
    itemNoVersionId?: string;
  }) => void;
  onOpenVersionsModal?: (itemNoVersionId: string) => void;
  isVersionsEnabled?: boolean;
}) => {
  const handleMenuClick = async (action: string) => {
    switch (action) {
      case "VERSIONS":
        if (onOpenVersionsModal && record?.info?.itemNoVersionId) {
          onOpenVersionsModal(record.info.itemNoVersionId);
        }
        break;
      case "SEND":
        if (handleAction) {
          await handleAction("SEND", { itemNoVersionId: record?.info?.itemNoVersionId });
        }
        break;
      case "REJECT":
        if (handleAction) {
          await handleAction("REJECT", { itemNoVersionId: record?.info?.itemNoVersionId });
        }
        break;
      case "DELETE":
        if (handleAction) {
          await handleAction("DELETE", { itemNoVersionId: record?.info?.itemNoVersionId });
        }
        break;
      case "RETURN":
        if (handleAction) {
          await handleAction("RETURN", { itemNoVersionId: record?.info?.itemNoVersionId });
        }
        break;
      case "EDIT":
        handleOpenForm({
          initTitle: "Редактирование записи о работе",
          isEdit: true,
          itemNoVersionId: record?.info?.itemNoVersionId,
        });
        break;
      case "RECALL":
        if (handleAction) {
          await handleAction("RECALL", { itemNoVersionId: record?.info?.itemNoVersionId });
        }
        break;
      case "VIEW":
        handleOpenFormInfo(record?.info?.itemNoVersionId);
        break;
      default:
        break;
    }
  };

  const baseItems = [
    {
      key: "view",
      label: "Открыть запись",
      icon: <EyeOutlined />,
      action: "VIEW",
    },
  ];

  const statusItemsWithEdit =
    getSectionThreeActionItems(
      record.status as RecordStatus,
      record,
      true,
      isVersionsEnabled,
    ) || [];

  const statusItemsViewOnly =
    getSectionThreeActionItems(
      record.status as RecordStatus,
      record,
      false,
      isVersionsEnabled,
    ) || [];

  const editMenuItems = [...baseItems, ...statusItemsWithEdit];
  const viewMenuItems = [...baseItems, ...statusItemsViewOnly];

  const makeMenuItems = (items: typeof editMenuItems) =>
    items.map((item) => ({
      key: item.key,
      label: (
        <div
          className="journal-table-menu-item"
          onClick={() => handleMenuClick(item.action)}
        >
          {item.icon}
          <span className="journal-table-menu-label">{item.label}</span>
        </div>
      ),
    }));

  const dropdownMenu = <Menu items={makeMenuItems(editMenuItems)} />;
  const dropdownViewMenu = <Menu items={makeMenuItems(viewMenuItems)} />;

  return (
    <>
      <Can I="edit" a="section_3">
        <Dropdown overlay={dropdownMenu} trigger={["click"]}>
          <div
            className="journal-table-dropdown-trigger"
            onClick={(event) => event.stopPropagation()}
          >
            <MoreOutlined />
          </div>
        </Dropdown>
      </Can>
      <Can not I="edit" a="section_3">
        <Dropdown overlay={dropdownViewMenu} trigger={["click"]}>
          <div
            className="journal-table-dropdown-trigger"
            onClick={(event) => event.stopPropagation()}
          >
            <MoreOutlined />
          </div>
        </Dropdown>
      </Can>
    </>
  );
};

const TableCellCompanies = ({
  companies,
}: {
  companies: CompanyItem[];
}) => {
  return (
    <div className="journal-table-companies-list">
      {renderCompaniesList(companies)}
    </div>
  );
};

const TableSellStatus = ({
  handleAction,
  localUser,
  mainJournalData,
  debouncedSearch,
  signingItemId,
  withSignedString,
  setupSigningString,
  signingString,
  record,
  bulkSendMode = false,
}: {
  handleAction?: (action: string, actionPayload?: any) => Promise<void>;
  localUser: any;
  mainJournalData: any;
  debouncedSearch: string;
  signingItemId: string | null;
  withSignedString: (params: any, signedXml: string, mchdId?: string) => Promise<void>;
  setupSigningString: (params: any, onOpen: () => void) => Promise<void>;
  signingString: string;
  record: any;
  bulkSendMode?: boolean;
}) => {
  const isMyEntry =
    record.info.signer.userId === localUser?.user_id &&
    record.status === "AWAITING_SIGNATURE";

  return (
    <div className="journal-table-signer-cell">
      <Space
        direction="vertical"
        size={0}
        style={{
          width: "100%",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div className="journal-table-signer-column">
          <div className="journal-table-signer-row">
            <UserBubble userId={record?.info?.signer?.userId}>
              <Button type="link" style={{ padding: 0, marginRight: 8 }}>
                {record.nameLink}
              </Button>
            </UserBubble>
            {statusIcons[record.status as keyof typeof statusIcons] && (
              <div className="journal-table-status-icon-wrap">
                {statusIcons[record.status as keyof typeof statusIcons]}
              </div>
            )}
            {record?.info?.comments.length > 0 && (
              <div className="journal-table-comment-wrap">
                <Tooltip
                  title={record?.info?.comments
                    .map((comment: any) => comment.comment)
                    .join("\n")}
                >
                  <span className="journal-table-comment-trigger">
                    <CommentChip className="comment-chip--table" />
                  </span>
                </Tooltip>
              </div>
            )}
          </div>
          <Text type="secondary" className="journals-section-third-table__signer-role journals-section-third-table__text">
            {record.role}
          </Text>
          {record.status === "SIGNED" && record?.info?.signer?.mchdId ? (
            <Text type="secondary" className="journals-section-third-table__text">
              Доверенность № {record.info.signer.mchdId}
            </Text>
          ) : null}
          {isMyEntry && !bulkSendMode && (
            <div className="journal-table-sign-actions">
              <Can I="sign" a="section_3">
                <JournalCryptographyWrapper
                  currentUser={localUser}
                  signContent={signingString}
                  onSuccess={(xml, _proxyId, mchdId) =>
                    withSignedString(
                      {
                        sectionThreeListId:
                          mainJournalData?.mainJournal?.section3
                            ?.sectionListId || "",
                        itemNoVersionId: record.info.itemNoVersionId,
                        signerEmployeeId: localUser?.user_id || "",
                        onSuccess: () =>
                          queryClient.invalidateQueries({
                            queryKey: journalsQueries.getSection3({
                              sectionThreeListId:
                                mainJournalData?.mainJournal?.section3
                                  ?.sectionListId || "",
                              search: debouncedSearch,
                            }).queryKey,
                          }),
                      },
                      xml,
                      mchdId,
                    )
                  }
                >
                  {({ onOpen }) => (
                      <Button
                      type="primary"
                      loading={signingItemId === record.info.itemNoVersionId}
                      style={{ marginRight: 10 }}
                      onClick={() =>
                        setupSigningString(
                          {
                            sectionThreeListId:
                              mainJournalData?.mainJournal?.section3
                                ?.sectionListId || "",
                            itemNoVersionId: record.info.itemNoVersionId,
                            signerEmployeeId: localUser?.user_id || "",
                          },
                          onOpen,
                        )
                      }
                    >
                      Подписать
                    </Button>
                  )}
                </JournalCryptographyWrapper>
              </Can>

              <Can I="sign" a="section_3">
                <Button
                  type="dashed"
                  onClick={async () => {
                    if (handleAction) {
                      await handleAction("REJECT", {
                        itemNoVersionId: record?.info?.itemNoVersionId,
                      });
                    }
                  }}
                >
                  Отклонить
                </Button>
              </Can>
            </div>
          )}
        </div>
      </Space>
    </div>
  );
};

export const getColumns = ({
  handleAction,
  handleOpenFormInfo,
  handleOpenForm,
  onOpenVersionsModal,
  isVersionsEnabled = true,
  localUser,
  mainJournalData,
  debouncedSearch,
  signingItemId,
  withSignedString,
  setupSigningString,
  signingString,
  bulkSendMode = false,
}: {
  handleAction?: (action: string, actionPayload?: any) => Promise<void>;
  handleOpenFormInfo: (itemNoVersionId: string) => void;
  handleOpenForm: ({ initTitle }: { initTitle: string }) => void;
  onOpenVersionsModal?: (itemNoVersionId: string) => void;
  isVersionsEnabled?: boolean;
  localUser: any;
  mainJournalData: any;
  debouncedSearch: string;
  signingItemId: string | null;
  withSignedString: (params: any, signedXml: string, mchdId?: string) => Promise<void>;
  setupSigningString: (params: any, onOpen: () => void) => Promise<void>;
  signingString: string;
  bulkSendMode?: boolean;
}) => [
  {
    title: "№",
    dataIndex: "number",
    key: "number",
    width: 88,
    render: (_: unknown, record: { number?: number; updateDate?: string }) =>
      renderNumberCell(record),
  },
  {
    title: renderColumnTitle("Наименование работ", COLUMN_TOOLTIPS.workName),
    dataIndex: "name",
    key: "name",
    width: 240,
    className: "journals-section-third-table__text-cell",
    render: (text: string) => (
      <span className="journals-section-third-table__text">{fallbackString(text)}</span>
    ),
  },
  {
    title: renderColumnTitle("Тип работы", COLUMN_TOOLTIPS.workType),
    dataIndex: "type",
    key: "type",
    width: 120,
    render: (text: string) => (
      <span className="journals-section-third-table__text">{fallbackString(text)}</span>
    ),
  },
  {
    title: renderColumnTitle("Дата выполнения", COLUMN_TOOLTIPS.workDate),
    dataIndex: "date",
    key: "date",
    width: 140,
    render: (date: string) => fallbackString(date),
  },
  {
    title: renderColumnTitle("Местоположение", COLUMN_TOOLTIPS.location),
    dataIndex: "location",
    key: "location",
    width: 240,
    className: "journals-section-third-table__text-cell",
    render: (text: string) => (
      <span className="journals-section-third-table__text">{fallbackString(text)}</span>
    ),
  },
  {
    title: renderColumnTitle("ИД", COLUMN_TOOLTIPS.actConnect),
    dataIndex: "id",
    key: "id",
    align: "center",
    width: 72,
    render: (_: unknown, record: { actConnect: boolean }) => (
      <div className="journal-table-connect-cell">
        <div
          className={clsx(
            "journals-section-third-table__id-flag",
            record?.actConnect
              ? "journals-section-third-table__id-flag--active"
              : "journals-section-third-table__id-flag--inactive",
          )}
        >
          {record?.actConnect ? <ConnectIcon /> : null}
        </div>
      </div>
    ),
  },
  {
    title: renderColumnTitle("Подписант", COLUMN_TOOLTIPS.signer),
    dataIndex: "status",
    key: "status",
    width: 280,
    render: (_: unknown, record: any) => (
      <TableSellStatus
        handleAction={handleAction}
        localUser={localUser}
        mainJournalData={mainJournalData}
        debouncedSearch={debouncedSearch}
        signingItemId={signingItemId}
        withSignedString={withSignedString}
        setupSigningString={setupSigningString}
        signingString={signingString}
        record={record}
        bulkSendMode={bulkSendMode}
      />
    ),
  },
  {
    title: renderColumnTitle(
      "Организации, осуществляющие работу",
      COLUMN_TOOLTIPS.companies,
    ),
    dataIndex: "companyList",
    key: "companyList",
    width: 260,
    render: (companies: CompanyItem[]) => (
      <TableCellCompanies companies={companies} />
    ),
  },
  {
    title: "Дата создания",
    dataIndex: "createDate",
    key: "createDate",
    width: 120,
    render: (_: unknown, record: any) => (
      <div className="journal-table-datetime-actions-cell">
        {renderDatetimeCell(record.info?.createDate ?? record.createDate)}
        {bulkSendMode ? null : (
          <TableCellActions
            record={record}
            handleAction={handleAction}
            handleOpenFormInfo={handleOpenFormInfo}
            handleOpenForm={handleOpenForm}
            onOpenVersionsModal={onOpenVersionsModal}
            isVersionsEnabled={isVersionsEnabled}
          />
        )}
      </div>
    ),
  },
];
